import React from 'react'
import { cn } from '../../utils'
import Container from './Container'

type SectionPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl'
type SectionBackground = 'default' | 'muted' | 'primary' | 'dark'

interface SectionProps {
  children: React.ReactNode
  className?: string
  containerClassName?: string
  padding?: SectionPadding
  background?: SectionBackground
  id?: string
  as?: 'section' | 'div' | 'article'
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
}

const paddingStyles: Record<SectionPadding, string> = {
  none: '',
  sm: 'py-8 md:py-12',
  md: 'py-12 md:py-16',
  lg: 'py-16 md:py-24',
  xl: 'py-24 md:py-32',
}

const backgroundStyles: Record<SectionBackground, string> = {
  default: 'bg-white dark:bg-gray-950',
  muted: 'bg-gray-50 dark:bg-gray-900',
  primary: 'bg-primary-400 dark:bg-primary-600 text-white',
  dark: 'bg-gray-900 dark:bg-gray-950 text-white',
}

interface SectionHeaderProps {
  title: string
  subtitle?: string
  description?: string
  align?: 'left' | 'center'
  className?: string
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  description,
  align = 'center',
  className,
}) => (
  <div className={cn(
    'mb-12 md:mb-16',
    align === 'center' && 'text-center',
    className
  )}>
    {subtitle && (
      <p className="text-sm font-medium text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-2">
        {subtitle}
      </p>
    )}
    <h2 className={cn(
      'text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 dark:text-white',
      align === 'center' && 'max-w-3xl mx-auto'
    )}>
      {title}
    </h2>
    {description && (
      <p className={cn(
        'mt-4 text-lg text-gray-600 dark:text-gray-400',
        align === 'center' && 'max-w-2xl mx-auto'
      )}>
        {description}
      </p>
    )}
  </div>
)

const Section: React.FC<SectionProps> & { Header: typeof SectionHeader } = ({
  children,
  className,
  containerClassName,
  padding = 'lg',
  background = 'default',
  id,
  as: Component = 'section',
  containerSize = 'xl',
}) => {
  return (
    <Component
      id={id}
      className={cn(
        paddingStyles[padding],
        backgroundStyles[background],
        'relative overflow-hidden',
        className
      )}
    >
      <Container size={containerSize} className={containerClassName}>
        {children}
      </Container>
    </Component>
  )
}

Section.Header = SectionHeader

export default Section
